// Trial Extension Constants
// Business rules for extended trial periods

export const TRIAL_EXTENSION_CONSTANTS = {
  // Extended trial end date: December 31, 2025 at 11:59:59 PM
  EXTENDED_TRIAL_END_DATE: new Date('2025-12-31T23:59:59.999Z'),
  
  // Cutoff date: December 17, 2025 at 00:00:00
  NEW_USER_CUTOFF_DATE: new Date('2025-12-17T00:00:00.000Z'),
  
  // Standard trial period for users registering after cutoff (14 days)
  STANDARD_TRIAL_DAYS: 14,
  
  // Professional plan ID for extended trials
  EXTENDED_TRIAL_PLAN_ID: 'professional',
  
  // Trial status
  TRIAL_STATUS: 'trial' as const,
  
  // Currency
  CURRENCY: 'EGP' as const,
  
  // Paid subscription periods
  MONTHLY_BILLING_DAYS: 30,
  YEARLY_BILLING_DAYS: 365
} as const;

// Helper functions for trial extension logic
export class TrialExtensionUtils {
  /**
   * Check if a given date is before the new user cutoff date
   */
  static isBeforeCutoffDate(date: Date): boolean {
    return date <= TRIAL_EXTENSION_CONSTANTS.NEW_USER_CUTOFF_DATE;
  }
  
  /**
   * Check if a given date is after the new user cutoff date
   */
  static isAfterCutoffDate(date: Date): boolean {
    return date > TRIAL_EXTENSION_CONSTANTS.NEW_USER_CUTOFF_DATE;
  }
  
  /**
   * Get the appropriate trial end date based on registration date
   * @param registrationDate - The date when the user registered
   * @returns The trial end date (14 days from registration)
   */
  static getTrialEndDate(registrationDate: Date): Date {
    // Extended trial period has ended (December 31, 2025)
    // All new users now get 14-day trial from their signup date
    const trialEndDate = new Date(registrationDate);
    trialEndDate.setDate(trialEndDate.getDate() + TRIAL_EXTENSION_CONSTANTS.STANDARD_TRIAL_DAYS);
    trialEndDate.setHours(23, 59, 59, 999); // Set to end of day
    return trialEndDate;
  }
  
  /**
   * Get the appropriate trial end date for existing users
   * @returns The extended trial end date (31/12/2025 23:59:59)
   */
  static getExtendedTrialEndDate(): Date {
    return TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE;
  }
  
  /**
   * Check if current date is within the extended trial period
   * @returns True if current date is before extended trial end date
   */
  static isWithinExtendedTrialPeriod(): boolean {
    return new Date() <= TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE;
  }
  
  /**
   * Get trial days remaining for extended trial
   * @param currentDate - Current date (defaults to now)
   * @returns Number of days remaining in extended trial
   */
  static getExtendedTrialDaysRemaining(currentDate: Date = new Date()): number {
    const endDate = TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE;
    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  }
  
  /**
   * Get trial days remaining for standard trial
   * @param startDate - Trial start date
   * @param currentDate - Current date (defaults to now)
   * @returns Number of days remaining in standard trial
   */
  static getStandardTrialDaysRemaining(startDate: Date, currentDate: Date = new Date()): number {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + TRIAL_EXTENSION_CONSTANTS.STANDARD_TRIAL_DAYS);
    endDate.setHours(23, 59, 59, 999);
    
    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  }
  
  /**
   * Check if a user should get extended trial based on registration date
   * @param registrationDate - The date when the user registered
   * @returns False (extended trial period has ended)
   */
  static shouldGetExtendedTrial(registrationDate: Date): boolean {
    // Extended trial period has ended (December 31, 2025)
    // All users now get standard 14-day trial
    return false;
  }
  
  /**
   * Get the appropriate plan ID for trial subscription
   * @returns Professional plan ID for extended trials
   */
  static getTrialPlanId(): string {
    return TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_PLAN_ID;
  }
  
  /**
   * Convert Date to milliseconds string (for Firestore compatibility)
   * @param date - Date to convert
   * @returns Date in milliseconds as string
   */
  static dateToMillisString(date: Date): string {
    return date.getTime().toString();
  }
  
  /**
   * Convert milliseconds string to Date
   * @param millisString - Date in milliseconds as string
   * @returns Date object
   */
  static millisStringToDate(millisString: string): Date {
    return new Date(parseInt(millisString));
  }
  
  /**
   * Calculate end date for paid subscriptions based on billing cycle
   * @param startDate - Subscription start date
   * @param billingCycle - 'monthly' or 'yearly'
   * @returns End date for the subscription
   */
  static getPaidSubscriptionEndDate(startDate: Date, billingCycle: 'monthly' | 'yearly' = 'monthly'): Date {
    const endDate = new Date(startDate);
    const daysToAdd = billingCycle === 'yearly' 
      ? TRIAL_EXTENSION_CONSTANTS.YEARLY_BILLING_DAYS 
      : TRIAL_EXTENSION_CONSTANTS.MONTHLY_BILLING_DAYS;
    
    endDate.setDate(endDate.getDate() + daysToAdd);
    endDate.setHours(23, 59, 59, 999); // Set to end of day
    return endDate;
  }
}
